import React, { useEffect, useState, useRef } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  FileText, 
  Folder, 
  FolderPlus,
  Upload,
  Search,
  Share2,
  Trash2,
  Download,
  Eye,
  Image,
  FileIcon,
  FolderOpen,
  ArrowLeft,
  List,
  MoreVertical,
  Users,
  Calendar,
  HardDrive
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RMKnowledgeCentre = ({ user, onLogout }) => {
  const [folders, setFolders] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState({ folders: [], files: [] });
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // Always list view
  
  // Dialog states
  const [newFolderDialogOpen, setNewFolderDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  
  // Form states
  const [newFolderName, setNewFolderName] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [breadcrumb, setBreadcrumb] = useState([]);
  
  // File upload
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Drag and drop states
  const [draggedItem, setDraggedItem] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);

  useEffect(() => {
    fetchFolders();
  }, []);

  const fetchFolders = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const response = await axios.get(`${API}/knowledge-centre/folders`, config);
      setFolders(response.data.folders || []);
    } catch (error) {
      console.error('Error fetching folders:', error);
      toast.error('Failed to fetch folders');
    } finally {
      setLoading(false);
    }
  };

  const handleItemDragStart = (e, item) => {
    setDraggedItem(item);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleItemDragOver = (e, targetFolder) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropTarget(targetFolder.id);
  };

  const handleItemDragLeave = (e) => {
    e.preventDefault();
    setDropTarget(null);
  };

  const handleItemDrop = async (e, targetFolder) => {
    e.preventDefault();
    setDropTarget(null);
    
    if (!draggedItem) return;
    
    try {
      // Here you would implement the move functionality
      toast.info(`Moving ${draggedItem.name} to ${targetFolder.name}`);
      setDraggedItem(null);
    } catch (error) {
      console.error('Error moving item:', error);
      toast.error('Failed to move item');
    }
  };

  const fetchFolderContents = async (folderId) => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const response = await axios.get(`${API}/knowledge-centre/folders/${folderId}/files`, config);
      setFiles(response.data.files || []);
      setCurrentFolder(response.data.folder);
    } catch (error) {
      console.error('Error fetching folder contents:', error);
      toast.error('Failed to fetch folder contents');
    }
  };

  const createFolder = async () => {
    if (!newFolderName.trim()) {
      toast.error('Please enter a folder name');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.post(`${API}/knowledge-centre/folders`, {
        name: newFolderName,
        parent_id: currentFolder?.id || null,
        is_shared: false
      }, config);
      
      toast.success('Folder created successfully');
      setNewFolderName('');
      setNewFolderDialogOpen(false);
      
      if (currentFolder) {
        await fetchFolderContents(currentFolder.id);
      } else {
        await fetchFolders();
      }
    } catch (error) {
      console.error('Error creating folder:', error);
      toast.error('Failed to create folder');
    }
  };

  const deleteFolder = async (folderId) => {
    if (window.confirm('Are you sure you want to delete this folder? All files inside will be deleted.')) {
      try {
        const token = localStorage.getItem('token');
        const config = { headers: { Authorization: `Bearer ${token}` } };
        
        await axios.delete(`${API}/knowledge-centre/folders/${folderId}`, config);
        toast.success('Folder deleted successfully');
        
        if (currentFolder) {
          await fetchFolderContents(currentFolder.id);
        } else {
          await fetchFolders();
        }
      } catch (error) {
        console.error('Error deleting folder:', error);
        toast.error('Failed to delete folder');
      }
    }
  };

  const deleteFile = async (fileId) => {
    if (window.confirm('Are you sure you want to delete this file?')) {
      try {
        const token = localStorage.getItem('token');
        const config = { headers: { Authorization: `Bearer ${token}` } };
        
        await axios.delete(`${API}/knowledge-centre/files/${fileId}`, config);
        toast.success('File deleted successfully');
        
        if (currentFolder) {
          await fetchFolderContents(currentFolder.id);
        }
      } catch (error) {
        console.error('Error deleting file:', error);
        toast.error('Failed to delete file');
      }
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults({ folders: [], files: [] });
      setHasSearched(false);
      return;
    }
    
    setIsSearching(true);
    setHasSearched(true);
    
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const response = await axios.get(`${API}/knowledge-centre/search?q=${encodeURIComponent(searchQuery)}`, config);
      console.log('Search response:', response.data);
      setSearchResults(response.data);
      setIsSearching(false);
    } catch (error) {
      console.error('Error searching:', error);
      // Fallback to local search if backend search fails
      const localSearchResults = {
        folders: folders.filter(folder => 
          folder.name.toLowerCase().includes(searchQuery.toLowerCase())
        ),
        files: files.filter(file => 
          file.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (file.description && file.description.toLowerCase().includes(searchQuery.toLowerCase()))
        )
      };
      setSearchResults(localSearchResults);
      setIsSearching(false);
      toast.info('Using local search - backend search unavailable');
    }
  };

  const openFolder = (folder) => {
    setBreadcrumb(prev => [...prev, folder]);
    setCurrentFolder(folder);
    fetchFolderContents(folder.id);
  };

  const navigateBack = () => {
    if (breadcrumb.length > 1) {
      const newBreadcrumb = breadcrumb.slice(0, -1);
      const parentFolder = newBreadcrumb[newBreadcrumb.length - 1];
      setBreadcrumb(newBreadcrumb);
      setCurrentFolder(parentFolder);
      fetchFolderContents(parentFolder.id);
    } else {
      setBreadcrumb([]);
      setCurrentFolder(null);
      setFiles([]);
      fetchFolders();
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const uploadFiles = async (fileList) => {
    if (!currentFolder) {
      toast.error('Please open a folder before uploading files');
      return;
    }
    setUploading(true);
    const token = localStorage.getItem('token');
    try {
      for (const file of fileList) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('folder_id', currentFolder.id);
        await axios.post(`${API}/knowledge-centre/upload`, formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
      }
      toast.success(`${fileList.length} file(s) uploaded successfully`);
      setUploadDialogOpen(false);
      await fetchFolderContents(currentFolder.id);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (fileId, fileName) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/knowledge-centre/files/${fileId}/download`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      window.open(response.data.download_url, '_blank');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download file');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      uploadFiles(Array.from(e.dataTransfer.files));
    }
  };

  const getFileIcon = (fileType) => {
    const iconProps = { className: "w-8 h-8" };
    
    switch (fileType?.toLowerCase()) {
      case 'pdf':
        return <FileText {...iconProps} className="w-8 h-8 text-red-500" />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return <Image {...iconProps} className="w-8 h-8 text-green-500" />;
      case 'doc':
      case 'docx':
        return <FileText {...iconProps} className="w-8 h-8 text-ugmonk-grey" />;
      default:
        return <FileIcon {...iconProps} className="w-8 h-8 text-gray-500" />;
    }
  };

  // Grid view removed - using list view only

  const renderListView = () => (
    <div className="space-y-2">
      {/* Folders - show root folders only when not in a folder */}
      {!currentFolder && folders.map((folder) => (
        <div 
          key={folder.id}
          className={`flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-200 ${
            dropTarget === folder.id ? 'border-[#ffd87a] bg-[#fffdf0]' : ''
          }`}
          draggable
          onDragStart={(e) => handleItemDragStart(e, folder)}
          onDragOver={(e) => handleItemDragOver(e, folder)}
          onDragLeave={handleItemDragLeave}
          onDrop={(e) => handleItemDrop(e, folder)}
        >
          <div className="flex items-center gap-3 flex-1">
            <FolderOpen className="w-6 h-6 text-ugmonk-dark" />
            <div>
              <p className="text-sm font-medium text-gray-900">{folder.name}</p>
              <p className="text-xs text-gray-500">Folder • {new Date(folder.created_at).toLocaleDateString()}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">
              {folder.is_shared ? 'Shared' : 'Private'}
            </Badge>
            <Button 
              size="sm" 
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                openFolder(folder);
              }}
              className="p-2 h-8 w-8"
              title="Open folder"
            >
              <FolderOpen className="w-4 h-4 text-ugmonk-dark" />
            </Button>
            <Button 
              size="sm" 
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedItem(folder);
                setShareDialogOpen(true);
              }}
              className="p-2 h-8 w-8"
              title="Share folder"
            >
              <Share2 className="w-4 h-4 text-ugmonk-grey" />
            </Button>
            <Button 
              size="sm" 
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                deleteFolder(folder.id);
              }}
              className="p-2 h-8 w-8"
              title="Delete folder"
            >
              <Trash2 className="w-4 h-4 text-red-500" />
            </Button>
          </div>
        </div>
      ))}

      {/* Files */}
      {files.map((file) => (
        <div 
          key={file.id}
          className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors duration-200"
          draggable
          onDragStart={(e) => handleItemDragStart(e, file)}
        >
          <div className="flex items-center gap-3 flex-1">
            {getFileIcon(file.file_type)}
            <div>
              <p className="text-sm font-medium text-gray-900">{file.name}</p>
              <p className="text-xs text-gray-500">
                {file.file_type?.toUpperCase()} • {(file.file_size / 1024).toFixed(1)} KB • {new Date(file.created_at).toLocaleDateString()}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">
              {file.is_shared ? 'Shared' : 'Private'}
            </Badge>
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                handleDownload(file.id, file.name);
              }}
              className="p-2 h-8 w-8"
              title="Download file"
            >
              <Download className="w-4 h-4 text-green-600" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedItem(file);
                setShareDialogOpen(true);
              }}
              className="p-2 h-8 w-8"
            >
              <Share2 className="w-4 h-4 text-ugmonk-grey" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                deleteFile(file.id);
              }}
              className="p-2 h-8 w-8"
            >
              <Trash2 className="w-4 h-4 text-red-500" />
            </Button>
          </div>
        </div>
      ))}

      {/* Upload Area - only show if we have folders or are in a folder */}
      {(folders.length > 0 || currentFolder) && (
        <div 
          className={`flex items-center justify-center p-6 border-2 border-dashed rounded-lg cursor-pointer transition-colors duration-200 ${
            dragActive ? 'border-[#ffd87a] bg-[#fffdf0]' : 'border-gray-300 hover:border-[#ffd87a]'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => setUploadDialogOpen(true)}
        >
          <Upload className="w-6 h-6 text-gray-400 mr-3" />
          <div>
            <p className="text-sm text-gray-600 font-medium">Upload File</p>
            <p className="text-xs text-gray-400">Click or drag files here</p>
          </div>
        </div>
      )}
    </div>
  );

  const renderSearchResults = () => (
    <div className="space-y-2">
      {/* Search Results - Folders */}
      {searchResults.folders.map((folder) => (
        <div 
          key={folder.id}
          className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-200"
        >
          <div className="flex items-center gap-3 flex-1">
            <FolderOpen className="w-6 h-6 text-ugmonk-dark" />
            <div>
              <p className="text-sm font-medium text-gray-900">{folder.name}</p>
              <p className="text-xs text-gray-500">Folder • {new Date(folder.created_at).toLocaleDateString()}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">
              {folder.is_shared ? 'Shared' : 'Private'}
            </Badge>
            <Button 
              size="sm" 
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                setSearchQuery('');
                setSearchResults({ folders: [], files: [] });
                openFolder(folder);
              }}
              className="p-2 h-8 w-8"
              title="Open folder"
            >
              <FolderOpen className="w-4 h-4 text-ugmonk-dark" />
            </Button>
            <Button 
              size="sm" 
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedItem(folder);
                setShareDialogOpen(true);
              }}
              className="p-2 h-8 w-8"
              title="Share folder"
            >
              <Share2 className="w-4 h-4 text-ugmonk-grey" />
            </Button>
          </div>
        </div>
      ))}

      {/* Search Results - Files */}
      {searchResults.files.map((file) => (
        <div 
          key={file.id}
          className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors duration-200"
        >
          <div className="flex items-center gap-3 flex-1">
            {getFileIcon(file.file_type)}
            <div>
              <p className="text-sm font-medium text-gray-900">{file.name}</p>
              <p className="text-xs text-gray-500">
                {file.file_type?.toUpperCase()} • {(file.file_size / 1024).toFixed(1)} KB • {new Date(file.created_at).toLocaleDateString()}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">
              {file.is_shared ? 'Shared' : 'Private'}
            </Badge>
            <Button 
              size="sm" 
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedItem(file);
                setShareDialogOpen(true);
              }}
              className="p-2 h-8 w-8"
            >
              <Share2 className="w-4 h-4 text-ugmonk-grey" />
            </Button>
          </div>
        </div>
      ))}

      {searchResults.folders.length === 0 && searchResults.files.length === 0 && (
        <div className="text-center py-8">
          <Search className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="text-gray-600">No items found matching "{searchQuery}"</p>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1a1a1a]"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2" style={{fontFamily: "'PerfectNineties', serif"}}>
              RM Knowledge Centre
            </h1>
            <p className="text-gray-600">Organize and manage your documents, reports, and files</p>
          </div>
          <div className="flex gap-3">
            <Dialog open={newFolderDialogOpen} onOpenChange={setNewFolderDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  className="font-semibold px-4 py-2 rounded-md transition-all duration-200 flex items-center gap-2 shadow-md hover:shadow-lg"
                  style={{ backgroundColor: '#1a1a1a', color: 'white', border: 'none' }}
                >
                  <FolderPlus className="w-4 h-4" />
                  New Folder
                </Button>
              </DialogTrigger>
              <DialogContent 
                className="max-w-md bg-white border border-gray-200 shadow-lg"
                style={{ backgroundColor: 'white', color: '#111827' }}
              >
                <DialogHeader>
                  <DialogTitle 
                    className="text-xl font-semibold text-gray-900"
                    style={{ color: '#111827' }}
                  >
                    Create New Folder
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label className="text-gray-900 font-medium mb-2 block" style={{ color: '#111827' }}>
                      Folder Name
                    </Label>
                    <Input
                      value={newFolderName}
                      onChange={(e) => setNewFolderName(e.target.value)}
                      placeholder="Enter folder name"
                      className="w-full p-3 border-2 border-gray-300 rounded-lg bg-white text-gray-900 focus:border-[#1a1a1a] focus:outline-none"
                      style={{ backgroundColor: 'white', color: '#111827' }}
                    />
                  </div>
                  <Button 
                    onClick={createFolder}
                    disabled={!newFolderName.trim()}
                    className="w-full font-semibold px-4 py-3 rounded-md transition-all duration-200 shadow-md hover:shadow-lg"
                    style={{ 
                      backgroundColor: !newFolderName.trim() ? '#9ca3af' : '#ffd87a', 
                      color: 'black',
                      cursor: !newFolderName.trim() ? 'not-allowed' : 'pointer',
                      border: 'none'
                    }}
                  >
                    Create Folder
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            <Button 
              onClick={() => setUploadDialogOpen(true)}
              className="font-semibold px-4 py-2 rounded-md transition-all duration-200 flex items-center gap-2 shadow-md hover:shadow-lg"
              style={{ backgroundColor: '#ffd87a', color: 'black', border: 'none' }}
            >
              <Upload className="w-4 h-4" />
              Upload File
            </Button>
          </div>
        </div>

        {/* Search and Navigation */}
        <div className="flex flex-col md:flex-row gap-4 items-center">
          <div className="flex items-center gap-2 flex-1">
            {breadcrumb.length > 0 && (
              <Button 
                variant="ghost" 
                onClick={navigateBack}
                className="p-2"
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
            )}
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <HardDrive className="w-4 h-4" />
              <span>Knowledge Centre</span>
              {breadcrumb.map((folder, index) => (
                <React.Fragment key={folder.id}>
                  <span>/</span>
                  <span>{folder.name}</span>
                </React.Fragment>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Input
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  if (!e.target.value.trim()) {
                    setSearchResults({ folders: [], files: [] });
                    setHasSearched(false);
                  }
                }}
                placeholder="Search files and folders..."
                className="w-64"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
                style={{ backgroundColor: 'white', color: '#111827' }}
              />
              <Button 
                onClick={handleSearch}
                disabled={!searchQuery.trim()}
                className="font-semibold px-4 py-2 rounded-md transition-all duration-200"
                style={{ 
                  backgroundColor: !searchQuery.trim() ? '#9ca3af' : '#1a1a1a',
                  color: 'white',
                  cursor: !searchQuery.trim() ? 'not-allowed' : 'pointer'
                }}
              >
                <Search className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <Card className="border-gray-200 bg-white">
          <CardContent className="p-6">
            {isSearching ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1a1a1a] mx-auto mb-4"></div>
                <p className="text-gray-600">Searching...</p>
              </div>
            ) : hasSearched ? (
              (searchResults.folders.length > 0 || searchResults.files.length > 0) ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Search Results for "{searchQuery}" ({searchResults.folders.length + searchResults.files.length} items)
                    </h3>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        setSearchQuery('');
                        setSearchResults({ folders: [], files: [] });
                        setHasSearched(false);
                      }}
                      className="text-gray-600"
                    >
                      Clear Search
                    </Button>
                  </div>
                  {renderSearchResults()}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Search className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg font-semibold text-gray-900">No results found for "{searchQuery}"</p>
                  <p className="text-gray-600">Try different keywords or check your spelling</p>
                  <Button 
                    onClick={() => {
                      setSearchQuery('');
                      setSearchResults({ folders: [], files: [] });
                      setHasSearched(false);
                    }}
                    className="mt-4 font-semibold px-4 py-2 rounded-md"
                    style={{ backgroundColor: '#ffd87a', color: 'black' }}
                  >
                    Clear Search
                  </Button>
                </div>
              )
            )
        : !currentFolder && folders.length === 0 ? (
              <div className="text-center py-16">
                <div className="max-w-md mx-auto">
                  <Folder className="w-20 h-20 mx-auto mb-6 text-gray-300" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Welcome to your Knowledge Centre</h3>
                  <p className="text-gray-600 mb-6">
                    Start organizing your documents by creating folders. You can create custom folders and organize your files just like on your computer.
                  </p>
                  <Button 
                    onClick={() => setNewFolderDialogOpen(true)}
                    className="font-semibold px-6 py-3 rounded-md shadow-lg hover:shadow-xl transition-all duration-200"
                    style={{ backgroundColor: '#ffd87a', color: 'black' }}
                  >
                    <FolderPlus className="w-5 h-5 mr-2" />
                    Create Your First Folder
                  </Button>
                </div>
              </div>
            ) : currentFolder && files.length === 0 ? (
              <div className="text-center py-12">
                <FolderOpen className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-semibold text-gray-900">Empty Folder</p>
                <p className="text-gray-600 mb-4">This folder is empty. Start by uploading files or creating subfolders.</p>
                <div className="flex justify-center gap-3">
                  <Button 
                    onClick={() => setUploadDialogOpen(true)}
                    className="font-semibold px-4 py-2 rounded-md"
                    style={{ backgroundColor: '#ffd87a', color: 'black' }}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Upload File
                  </Button>
                  <Button 
                    onClick={() => setNewFolderDialogOpen(true)}
                    variant="outline"
                    className="font-semibold px-4 py-2 rounded-md border-[#1a1a1a] text-ugmonk-dark"
                  >
                    <FolderPlus className="w-4 h-4 mr-2" />
                    New Folder
                  </Button>
                </div>
              </div>
            ) : (
              renderListView()
            )}
          </CardContent>
        </Card>

        {/* Upload Dialog */}
        <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
          <DialogContent 
            className="max-w-lg bg-white border border-gray-200 shadow-lg"
            style={{ backgroundColor: 'white', color: '#111827' }}
          >
            <DialogHeader>
              <DialogTitle 
                className="text-xl font-semibold text-gray-900"
                style={{ color: '#111827' }}
              >
                Upload File
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div 
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  dragActive ? 'border-[#ffd87a] bg-[#fffdf0]' : 'border-gray-300'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <p className="text-lg font-medium text-gray-900 mb-2">
                  Drop files here or click to browse
                </p>
                <p className="text-sm text-gray-600">
                  Supports all file types • No size limit
                </p>
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading || !currentFolder}
                  className="mt-4 font-semibold px-4 py-2 rounded-md"
                  style={{ backgroundColor: '#ffd87a', color: 'black' }}
                >
                  {uploading ? 'Uploading...' : 'Choose Files'}
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    if (e.target.files && e.target.files.length > 0) {
                      uploadFiles(Array.from(e.target.files));
                      e.target.value = '';
                    }
                  }}
                />
              </div>
              {!currentFolder && (
                <p className="text-sm text-amber-600">
                  <strong>Note:</strong> Please open a folder first before uploading files.
                </p>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Share Dialog */}
        <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
          <DialogContent 
            className="max-w-md bg-white border border-gray-200 shadow-lg"
            style={{ backgroundColor: 'white', color: '#111827' }}
          >
            <DialogHeader>
              <DialogTitle 
                className="text-xl font-semibold text-gray-900"
                style={{ color: '#111827' }}
              >
                Share {selectedItem?.name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Share this {selectedItem ? (selectedItem.file_type ? 'file' : 'folder') : 'item'} with other RMs
              </p>
              <div>
                <Label className="text-gray-900 font-medium mb-2 block" style={{ color: '#111827' }}>
                  Select RMs to share with
                </Label>
                {/* RM selection would go here - integrate with RM list */}
                <p className="text-sm text-gray-500">RM selection integration needed</p>
              </div>
              <Button 
                className="w-full font-semibold px-4 py-2 rounded-md"
                style={{ backgroundColor: '#1a1a1a', color: 'white' }}
                onClick={() => {
                  toast.info('Share functionality - integrate with RM selection');
                  setShareDialogOpen(false);
                }}
              >
                Share
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default RMKnowledgeCentre;
